import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { deleteReport } from '../api/reports';
import { deleteSession, fetchSessions, type SessionRow, type SessionsList } from '../api/sessions';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import { MissionCover, TypeTile } from './MissionCover';
import { PlannedChip } from './Planned';
import { Shell } from './Shell';
import { WhenLine } from './SessionWhen';
import { useNow } from './useNow';
import { PageLoading, PageProblem } from './PageStates';
import { useRetryKey } from './connectionHelpers';
import { TerrainImage } from './TerrainImage';
import { useConfirm } from './useConfirm';

const when = (s: SessionRow) => s.startedAt ?? s.scheduledAt ?? s.endedAt ?? '';
const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('ca', { dateStyle: 'medium', timeStyle: 'short' }) : null;

/** What a selection holds a row by: a session's code, or a match's id when the row has no session. */
const keyOf = (s: SessionRow) => s.code ?? `m${s.reportId ?? ''}`;

const GROUPS: { key: 'live' | 'scheduled' | 'ended'; state: SessionRow['state'] }[] = [
  { key: 'live', state: 'LIVE' },
  { key: 'scheduled', state: 'SCHEDULED' },
  { key: 'ended', state: 'ENDED' },
];

function matches(s: SessionRow, q: string, mine: boolean, tests: boolean): boolean {
  if (mine && !s.played) return false;
  // A test (a smoke night, an inspection tour, a test card's session) is the
  // platform's own work, not a night anybody played: the server leaves it out
  // unless an admin asked, and so does the page while the answer it holds is
  // the one with the tests in.
  if (!tests && (s.test === true || s.smoke === true)) return false;
  if (!q) return true;
  const hay =
    `${s.missionTitle} ${s.worldDisplayName ?? ''} ${s.worldName ?? ''} ${s.serverName ?? ''}`.toLowerCase();
  return hay.includes(q.toLowerCase());
}

/**
 * Every night the account can see (UI doc section 4, "Sessions" in the rail):
 * the platform's sessions and the panel's matches, from `GET /api/sessions`,
 * in three groups. A member sees every scheduled or live one and, of the
 * ended ones, those they played. The cards follow the library's shape: a
 * terrain thumbnail with the state over it, then the night's facts.
 */
export function Sessions() {
  const { me } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<SessionsList | null>(null);
  const [error, setError] = useState(false);
  const [confirm, confirmDialog] = useConfirm();
  const t = ca.sessionsPage2;
  const admin = me.roles.includes('ADMIN');
  const q = params.get('q') ?? '';
  const mine = params.get('mine') === '1';
  // "smoke" is the old name of the same switch, kept for the links that carry it.
  const tests = admin && (params.get('proves') === '1' || params.get('smoke') === '1');
  const [hiddenTests, setHiddenTests] = useState(0);
  // An admin's clean-up (2026-09-19): an installation gathers a test night for
  // every build, and one card at a time is no way to take a hundred of them out.
  const [selecting, setSelecting] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [alsoReports, setAlsoReports] = useState(true);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [failures, setFailures] = useState(0);
  /** The list did not arrive: said so, never drawn as an empty list. */
  const [loadError, setLoadError] = useState<unknown>(null);
  const [attempt, retry] = useRetryKey();
  // The countdowns of the nights that wait: minutes are what they show, so twice a minute is enough.
  const now = useNow(30_000);

  useEffect(() => {
    if (!me.authenticated) return;
    setLoadError(null);
    fetchSessions(tests)
      .then((d) => {
        setData({ rows: d.rows ?? [], canSeeAll: !!d.canSeeAll });
        if (!tests) setHiddenTests(d.hiddenTests ?? 0);
      })
      .catch((err) => {
        setData(null);
        setLoadError(err);
        setError(true);
      });
  }, [me.authenticated, tests, attempt]);

  /**
   * Removes a night and takes its card off the list. Only the platform's own
   * sessions: a row from the panel has no code of ours and belongs to the
   * panel's records, which the reports page removes.
   */
  async function remove(row: SessionRow) {
    if (!row.code) return;
    const yes = await confirm({
      title: t.deleteTitle(row.missionTitle),
      body: t.deleteBody,
      confirmLabel: t.deleteConfirm,
      danger: true,
    });
    if (!yes) return;
    try {
      await deleteSession(row.code);
      setData((d) => (d ? { ...d, rows: d.rows.filter((x) => x.code !== row.code) } : d));
    } catch {
      setError(true);
    }
  }

  const toggle = (row: SessionRow) =>
    setPicked((old) => {
      const next = new Set(old);
      const key = keyOf(row);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  /**
   * Removes every picked night, one request at a time: the panel is asked about
   * each report, and a hundred at once is not something to do to it. A session
   * goes with its own report when asked to, since a report left behind comes
   * back to this list as a night of its own.
   */
  async function removeMany(list: SessionRow[]) {
    const yes = await confirm({
      title: t.deleteManyTitle(list.length),
      body: t.deleteManyBody(alsoReports),
      confirmLabel: t.deleteMany(list.length),
      danger: true,
    });
    if (!yes) return;
    let failed = 0;
    const gone = new Set<string>();
    setFailures(0);
    setProgress({ done: 0, total: list.length });
    for (const [i, row] of list.entries()) {
      try {
        if (row.code) await deleteSession(row.code);
        if (row.reportId != null && (alsoReports || !row.code)) await deleteReport(row.reportId);
        gone.add(keyOf(row));
      } catch {
        failed += 1;
      }
      setProgress({ done: i + 1, total: list.length });
    }
    setData((d) => (d ? { ...d, rows: d.rows.filter((x) => !gone.has(keyOf(x))) } : d));
    setPicked(new Set());
    setProgress(null);
    setFailures(failed);
  }

  const setFilter = (key: 'q' | 'mine' | 'proves', value: string) => {
    const next = new URLSearchParams(params);
    if (key === 'proves') next.delete('smoke');
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const rows = useMemo(
    () => (data?.rows ?? []).filter((s) => matches(s, q, mine, tests)),
    [data, q, mine, tests],
  );
  // What a selection may hold: what the filters show, never a night in play, and
  // only a row there is something to delete behind.
  const selectable = useMemo(
    () => rows.filter((s) => s.state !== 'LIVE' && (s.code || s.reportId != null)),
    [rows],
  );
  const pickedRows = useMemo(
    () => selectable.filter((s) => picked.has(keyOf(s))),
    [selectable, picked],
  );

  return (
    <Shell title={ca.sessionsPage.title}>
      <p className="muted">
        {ca.sessionsPage.intro} <PlannedChip id="sessions-reports-one-page" named />
      </p>
      {!me.authenticated && <p className="muted">{ca.signInToSee}</p>}
      {me.authenticated && !data && loadError === null && <PageLoading />}
      {loadError !== null && <PageProblem error={loadError} onRetry={retry} />}
      {data && !data.canSeeAll && !error && <p className="muted small">{t.onlyMineHint}</p>}
      {data && (data.rows.length > 0 || admin) && (
        <div className="toolbar sessions-filters" role="group" aria-label={t.filters}>
          <button
            type="button"
            className={`chip toggle ${mine ? 'on' : ''}`}
            aria-pressed={mine}
            onClick={() => setFilter('mine', mine ? '' : '1')}
          >
            <i className="bi bi-person-check" aria-hidden="true" />
            {t.onlyPlayed}
          </button>
          {admin && (
            <button
              type="button"
              className={`chip toggle ${tests ? 'on' : ''}`}
              aria-pressed={tests}
              title={t.testsHint}
              onClick={() => setFilter('proves', tests ? '' : '1')}
            >
              <i className="bi bi-clipboard-check" aria-hidden="true" />
              {hiddenTests > 0 ? t.testsCount(hiddenTests) : t.tests}
            </button>
          )}
          {admin && (
            <button
              type="button"
              className={`chip toggle ${selecting ? 'on' : ''}`}
              aria-pressed={selecting}
              onClick={() => {
                setSelecting(!selecting);
                setPicked(new Set());
              }}
            >
              <i className="bi bi-check2-square" aria-hidden="true" />
              {selecting ? t.selectDone : t.select}
            </button>
          )}
          <span className="grow" />
          <input
            type="search"
            className="control"
            aria-label={t.search}
            placeholder={t.searchPlaceholder}
            value={q}
            onChange={(e) => setFilter('q', e.target.value)}
          />
        </div>
      )}
      {selecting && (
        <div className="toolbar sessions-bulk" role="group" aria-label={t.select}>
          <button
            type="button"
            className="button small ghost"
            onClick={() => setPicked(new Set(selectable.map(keyOf)))}
          >
            {t.selectAll(selectable.length)}
          </button>
          <button type="button" className="button small ghost" onClick={() => setPicked(new Set())}>
            {t.selectNone}
          </button>
          <label className="check small">
            <input
              type="checkbox"
              checked={alsoReports}
              onChange={(e) => setAlsoReports(e.target.checked)}
            />
            {t.alsoReports}
          </label>
          <span className="grow" />
          {progress && (
            <span className="muted small num" role="status">
              {t.deleting(progress.done, progress.total)}
            </span>
          )}
          <button
            type="button"
            className="button small danger"
            disabled={pickedRows.length === 0 || !!progress}
            onClick={() => void removeMany(pickedRows)}
          >
            <i className="bi bi-trash" aria-hidden="true" />
            {t.deleteMany(pickedRows.length)}
          </button>
        </div>
      )}
      {failures > 0 && <p className="notice warn small">{t.deleteFailed(failures)}</p>}
      {data && data.rows.length === 0 && (
        <div className="empty">
          <i className="bi bi-calendar3" aria-hidden="true" />
          <p className="muted">
            {ca.sessionsPage.empty} {ca.sessionsPage.emptyHint}
          </p>
        </div>
      )}
      {data && data.rows.length > 0 && rows.length === 0 && (
        <div className="empty">
          <i className="bi bi-funnel" aria-hidden="true" />
          <p className="muted">{t.noMatch}</p>
        </div>
      )}
      {GROUPS.map(({ key, state }) => {
        const list = rows
          .filter((s) => s.state === state)
          .sort((a, b) =>
            state === 'ENDED' ? when(b).localeCompare(when(a)) : when(a).localeCompare(when(b)),
          );
        if (list.length === 0) return null;
        return (
          <section key={key} aria-label={ca.sessionsPage[key]}>
            <h2 className="section">
              {ca.sessionsPage[key]} · {list.length}
            </h2>
            <div className="cards grid sessions">
              {list.map((s, i) => (
                <SessionCard
                  key={s.code ?? `panel-${s.reportId ?? i}`}
                  s={s}
                  now={now}
                  onDelete={
                    !selecting && admin && s.kind === 'platform' && s.code
                      ? () => void remove(s)
                      : null
                  }
                  picked={selecting && selectable.includes(s) ? picked.has(keyOf(s)) : null}
                  onPick={() => toggle(s)}
                />
              ))}
            </div>
          </section>
        );
      })}
      {confirmDialog}
    </Shell>
  );
}

/** One night as a library-shaped card: the terrain, the state over it, the facts and the links. */
function SessionCard({
  s,
  now,
  onDelete,
  picked = null,
  onPick,
}: {
  /** The clock the countdown of a night that waits reads. */
  now: number;
  /** Null while nobody is selecting, or for a row a selection cannot hold. */
  picked?: boolean | null;
  onPick?: () => void;
  s: SessionRow;
  /** Null for everybody but an admin looking at one of the platform's own sessions. */
  onDelete?: (() => void) | null;
}) {
  const t = ca.sessionsPage2;
  const panel = s.kind !== 'platform' || !s.code;
  const orphan = s.kind === 'orphan';
  const live = s.state === 'LIVE';
  const noPicture = s.recipe ? (
    // The mission's own cover, so a night on a world with no picture is
    // still the mission it is and not a grey box with a class name in it.
    <TypeTile recipe={s.recipe} icon={s.recipeIcon} label={s.recipeTitle} />
  ) : (
    <div className="thumb placeholder">{s.worldDisplayName ?? s.worldName ?? t.noTerrain}</div>
  );
  const meta = [s.worldDisplayName ?? s.worldName, s.serverName]
    .filter((x): x is string => !!x)
    .join(' · ');
  return (
    <article
      className={`card mission session ${live ? 'live' : ''} ${s.played ? 'mine' : ''} ${picked ? 'picked' : ''}`}
      aria-label={s.missionTitle}
    >
      {picked !== null && (
        <label className="pick-box">
          <input
            type="checkbox"
            checked={picked}
            onChange={onPick}
            aria-label={t.selectCard(s.missionTitle)}
          />
        </label>
      )}
      <div className="thumb-wrap">
        {s.missionCode && s.missionRevision != null ? (
          // The mission's own picture, as its card in the library has it.
          <MissionCover
            code={s.missionCode}
            revision={s.missionRevision}
            world={s.worldName}
            worldLabel={s.worldDisplayName ?? s.worldName}
            recipe={s.recipe}
            recipeIcon={s.recipeIcon}
            recipeTitle={s.recipeTitle}
            cover={s.cover ?? 'AUTO'}
          />
        ) : s.worldName ? (
          // The same picture, the same asking again and the same placeholder as
          // every other card: a bare <img> here gave up at the first 404 and
          // stayed a placeholder until the page was reloaded.
          <TerrainImage world={s.worldName} size="tile" fallback={noPicture} />
        ) : (
          noPicture
        )}
        <span className={`chip state ${live ? 'live' : ''}`}>
          {live && <span className="pulse" aria-hidden="true" />}
          {ca.sessionStates[s.state] ?? s.state}
        </span>
      </div>
      <div className="body">
        <strong title={s.missionFile ?? undefined}>{s.missionTitle}</strong>
        {meta && <span className="muted small">{meta}</span>}
        {s.state === 'SCHEDULED' && !panel ? (
          // A night that waits: its date as a tile and the time left, ticking.
          <WhenLine scheduledAt={s.scheduledAt} now={now} />
        ) : (
          <span className="muted small num">{fmt(when(s) || null) ?? ca.sessionsPage.undated}</span>
        )}
        {s.state === 'ENDED' && (
          <span className="muted small num">
            {ca.reportPlayers(s.playerCount ?? 0)}
            {s.durationSeconds != null && ` · ${ca.reportDuration(s.durationSeconds)}`}
          </span>
        )}
        {(s.outcome || s.played || panel || s.smoke || s.test) && (
          <div className="row chips">
            {s.test && !s.smoke && (
              <span className="chip" title={t.testsHint}>
                <i className="bi bi-clipboard-check" aria-hidden="true" />
                {t.testChip}
              </span>
            )}
            {s.smoke && (
              <span className="chip" title={t.smokeNightHint}>
                <i className="bi bi-fire" aria-hidden="true" />
                {t.smokeNight}
              </span>
            )}
            {s.outcome && (
              <span
                className={`chip ${s.outcome === 'success' ? 'ok' : s.outcome === 'failure' || s.outcome === 'failed' ? 'bad' : s.outcome === 'partial' ? 'warn' : ''}`}
              >
                {ca.outcomes[s.outcome] ?? s.outcome}
              </span>
            )}
            {s.played && (
              <span className="chip played">
                <i className="bi bi-check2" aria-hidden="true" />
                {t.played}
              </span>
            )}
            {panel && (
              <span className="chip" title={orphan ? t.fromPlatformHint : t.fromPanelHint}>
                <i className={`bi ${orphan ? 'bi-box' : 'bi-hdd-network'}`} aria-hidden="true" />
                {orphan ? t.fromPlatform : t.fromPanel}
              </span>
            )}
          </div>
        )}
        <div className="row actions">
          {!panel && s.code && (
            <Link className="button small" to={`/s/${s.code}`}>
              <i className="bi bi-play-circle" aria-hidden="true" />
              {t.open}
            </Link>
          )}
          {!panel && s.code && s.state !== 'ENDED' && (
            <Link className="button small ghost" to={`/s/${s.code}/briefing`}>
              <i className="bi bi-journal-text" aria-hidden="true" />
              {t.briefing}
            </Link>
          )}
          {s.reportUrl && (
            <a className="button small" href={s.reportUrl} target="_blank" rel="noreferrer">
              <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
              {t.report}
            </a>
          )}
          {s.pdfUrl && (
            <a className="button small ghost" href={s.pdfUrl} target="_blank" rel="noreferrer">
              <i className="bi bi-file-earmark-pdf" aria-hidden="true" />
              {t.pdf}
            </a>
          )}
          {onDelete && (
            <button
              type="button"
              className="button small danger"
              onClick={onDelete}
              aria-label={t.deleteLabel(s.missionTitle)}
              disabled={live}
              title={live ? t.deleteLiveHint : undefined}
            >
              <i className="bi bi-trash" aria-hidden="true" />
              {t.delete}
            </button>
          )}
        </div>
      </div>
    </article>
  );
}
